import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@mui/material";
import { Link } from "react-router-dom";
import { SquarePen, Trash } from "lucide-react";
import { withSwal } from 'react-sweetalert2'
import Api from "../../utils/Api";
import { Spin } from "antd";

function Categories({ swal }) {
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(false)
    const [openDialog, setOpenDialog] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [updatedTitle, setUpdatedTitle] = useState("");

    const fetchCategories = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await Api.get("/categories");
            setCategories(response.data.data);
        } catch (error) {
            console.error("Failed to fetch categories", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, []);

    const handleOpenDialog = (category) => {
        setCurrentCategory(category);
        setUpdatedTitle(category.category_name);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentCategory(null);
    };

    const handleUpdateCategory = async () => {
        try {
            // Update the category through the API
            await Api.put(`/update-category/${currentCategory.id}`, { category_name: updatedTitle });
            // Update the local state to reflect changes
            setCategories(categories.map(cat => (cat.id === currentCategory.id ? { ...cat, category_name: updatedTitle } : cat)));
            handleCloseDialog();
        } catch (error) {
            console.error("Failed to update category", error);
        }
    };

    const handleDeleteCategory = async (id) => {
        try {
            swal.fire({
                // title: 'Are you sure?',
                title: 'Bist du sicher?',
                text: `Möchten Sie '${categories.find(cat => cat.id === id)?.category_name}' löschen?`,
                showCancelButton: true,
                cancelButtonText: 'Abbrechen',
                confirmButtonText: 'Ja, löschen!',
                confirmButtonColor: '#d55',  // Matching the green theme
                cancelButtonColor: '#000',      // Black for the cancel button
                reverseButtons: true,
                background: '#f2f2f2',          // Light background for contrast
                color: '#000',                  // Dark text for readability
                customClass: {
                    title: 'swal-title',        // Apply custom CSS classes (defined below)
                    confirmButton: 'swal-confirm-button',
                    cancelButton: 'swal-cancel-button',
                },
            }).then(async result => {
                if (result.isConfirmed) {
                    await Api.delete(`/delete-category/${id}`);
                    fetchCategories();
                    // setCategories(categories.filter(cat => cat.id !== id));
                }
            });
        } catch (error) {
            console.error("Failed to delete category", error.message);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                position: 'relative'
                // minHeight: "84vh",
                // background: "linear-gradient(to left, #0BD349, #000000)",
            }}
        >
            <img style={{
                width: '100%',
                objectFit: 'contain',
                position: 'fixed',
                zIndex: -1
            }} src="https://cdn.prod.website-files.com/65f03c978efea1c906592c94/65f41d5f2ba41fbbe6c5514c_AdobeStock_546815906_gruen_klein_bearbeitet_002.webp" />
            <Container>
                <Typography
                    variant="h4"
                    component="h1"
                    gutterBottom
                    sx={{
                        fontWeight: "bold",
                        color: "#0BD349",
                        paddingTop: "5px",
                    }}
                >
                    {/* Categories */}
                    Kategorien
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        paddingBottom: "16px",
                    }}
                >
                    <Link to="/dashboard/new-category">
                        <Button
                            variant="contained"
                            sx={{
                                backgroundColor: "#fff",
                                color: "#333",
                                padding: "12px 24px",
                                fontSize: "16px",
                                borderRadius: "8px",
                                "&:hover": {
                                    backgroundColor: "#098B2B",
                                    // color: "#fff",
                                },
                                // border: '1px solid #eee',
                            }}
                        >
                            {/* Add New Category */}
                            Neue Kategorie hinzufügen
                        </Button>
                    </Link>
                </Box>

                <TableContainer component={Paper} sx={{ marginBottom: 3, backgroundColor: 'rgb(28, 28, 28, 0.9)' }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{
                                    backgroundColor: 'rgb(0, 0, 0, 0.6)',
                                    color: '#0BD349'
                                }}>
                                    {/* Category Title */}
                                    Kategorietitel
                                </TableCell>
                                <TableCell style={{
                                    backgroundColor: 'rgb(0, 0, 0, 0.6)',
                                    color: '#0BD349'
                                }}>
                                    {/* Actions */}
                                    Aktionen
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                isLoading && (
                                    <TableRow>
                                        <TableCell colSpan={2}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}>
                                                <Spin spinning={isLoading} />
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                            {categories.map((category) => (
                                <TableRow key={category.id}>
                                    <TableCell>{category.category_name}</TableCell>
                                    <TableCell>
                                        <Button className="link" onClick={() => handleOpenDialog(category)}>
                                            <SquarePen size={20} strokeWidth={1.5} />
                                        </Button>
                                        <Button className="link" onClick={() => handleDeleteCategory(category.id)}>
                                            <Trash size={20} strokeWidth={1.5} />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Dialog open={openDialog} onClose={handleCloseDialog}>
                    <DialogTitle>
                        {/* Update Category */}
                        Kategorie aktualisieren
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            // label="Category Title"
                            label="Kategorietitel"
                            type="text"
                            fullWidth
                            variant="standard"
                            value={updatedTitle}
                            onChange={(e) => setUpdatedTitle(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Stornieren</Button>
                        <Button onClick={handleUpdateCategory}>Aktualisieren</Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
}

export default withSwal(({ swal }, ref) => (
    <Categories swal={swal} />
));